<template>
  <v-container fluid class="pa-4">
    <!-- ✅ HEADER PRINCIPAL -->
    <div class="page-header mb-4">
      <div class="d-flex flex-column">
        <div class="d-flex align-center flex-wrap">
          <div class="d-flex align-center">
            <v-avatar size="48" color="teal-darken-1" class="mr-3" variant="tonal">
              <v-icon color="white" size="28">mdi-application</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h4 font-weight-bold text-grey-darken-4 mb-1">Gerenciar Aplicações</h1>
              <div class="text-caption text-grey">
                Cadastre aplicações do sistema e gerencie permissões por grupo
              </div>
            </div>
          </div>
          
          <v-spacer></v-spacer>
          
          <div class="d-flex align-center mt-2 mt-sm-0">
            <v-btn
              color="grey-darken-1"
              variant="text"
              @click="voltarMenu"
              class="text-none mr-2"
              prepend-icon="mdi-arrow-left"
            >
              Voltar
            </v-btn>
            
            <v-btn
              color="teal-darken-1"
              variant="flat"
              @click="carregarAplicacoes"
              class="text-none mr-2"
              prepend-icon="mdi-refresh"
              :loading="carregando"
            >
              Atualizar
            </v-btn>

            <v-btn
              color="primary"
              variant="flat"
              @click="abrirDialogNovo"
              class="text-none"
              prepend-icon="mdi-plus"
            >
              Nova Aplicação
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <v-card class="mb-4">
      <v-card-title class="d-flex align-center pa-4 bg-grey-lighten-4">
        <v-icon left>mdi-table</v-icon>
        <span class="text-h6 ml-2">Lista de Aplicações</span>
        <v-spacer></v-spacer>
        <v-chip color="primary" variant="tonal">
          {{ aplicacoes.length }} aplicações
        </v-chip>
      </v-card-title>

      <v-card-text>
        <!-- Filtros -->
        <v-row class="mb-4">
          <!-- Linha 1 -->
          <v-col cols="12" md="4">
            <v-text-field
              v-model="busca"
              prepend-inner-icon="mdi-magnify"
              label="Buscar aplicação"
              placeholder="Digite app_name ou descrição..."
              variant="outlined"
              density="compact"
              clearable
              hide-details
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              v-model="filtroTipo"
              :items="tiposDisponiveis"
              label="Tipo"
              prepend-inner-icon="mdi-filter"
              variant="outlined"
              density="compact"
              clearable
              hide-details
            ></v-select>
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="filtroPai"
              :items="paisDisponiveis"
              item-title="label"
              item-value="app_name"
              label="Pai"
              prepend-inner-icon="mdi-folder"
              variant="outlined"
              density="compact"
              clearable
              hide-details
            ></v-select>
          </v-col>

          <v-col cols="12" md="3" class="d-flex align-center">
            <v-btn
              color="primary"
              variant="tonal"
              size="small"
              @click="limparFiltros"
              block
            >
              <v-icon start size="small">mdi-filter-off</v-icon>
              Limpar Filtros
            </v-btn>
          </v-col>

          <!-- Linha 2: Datas -->
          <v-col cols="12" md="3">
            <v-text-field
              v-model="dataInicial"
              label="Data Inicial"
              type="date"
              prepend-inner-icon="mdi-calendar-start"
              variant="outlined"
              density="compact"
              clearable
              hide-details
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="dataFinal"
              label="Data Final"
              type="date"
              prepend-inner-icon="mdi-calendar-end"
              variant="outlined"
              density="compact"
              clearable
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Tabela de Aplicações -->
        <v-data-table
          :headers="headers"
          :items="aplicacoesFiltradas"
          :search="busca"
          :loading="carregando"
          class="elevation-1"
          :items-per-page="15"
        >
          <!-- Descrição -->
          <template #[`item.description`]="{ item }">
            <div class="text-body-2">
              {{ item.description || 'Sem descrição' }}
            </div>
          </template>

          <!-- Nome da Aplicação -->
          <template #[`item.app_name`]="{ item }">
            <div class="font-weight-bold font-monospace text-primary">
              {{ item.app_name }}
            </div>
          </template>

          <!-- Tipo -->
          <template #[`item.app_type`]="{ item }">
            <v-chip size="small" :color="getTypeColor(item.app_type)">
              {{ item.app_type || 'N/A' }}
            </v-chip>
          </template>

          <!-- Pai -->
          <template #[`item.parent_app`]="{ item }">
            <div v-if="item.parent_app">
              <v-icon size="small" color="blue" class="mr-1">mdi-folder</v-icon>
              <span class="text-caption">{{ item.parent_app }}</span>
            </div>
            <span v-else class="text-grey text-caption">-</span>
          </template>

          <!-- Ordem -->
          <template #[`item.order`]="{ item }">
            <v-chip v-if="item.order" size="x-small" color="grey" variant="tonal">
              {{ item.order }}
            </v-chip>
            <span v-else class="text-grey">-</span>
          </template>

          <!-- Grupos vinculados -->
          <template #[`item.groups`]="{ item }">
            <v-chip color="blue-lighten-3" size="small">
              {{ item.groups ? item.groups.length : 0 }} grupos
            </v-chip>
          </template>

          <!-- Ações -->
          <template #[`item.actions`]="{ item }">
            <v-btn icon size="small" @click="editarAplicacao(item)" color="primary" title="Editar">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon size="small" @click="confirmarExclusao(item)" color="error" title="Excluir">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dialog de Criar/Editar Aplicação -->
    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ modoEdicao ? 'Editar Aplicação' : 'Nova Aplicação' }}</span>
        </v-card-title>

        <v-card-text>
          <v-form ref="form" v-model="formValido">
            <v-text-field
              v-model="aplicacaoAtual.app_name"
              label="Nome da Aplicação"
              :rules="[v => !!v || 'Nome é obrigatório']"
              hint="Use snake_case, ex: dashboard_producao"
              persistent-hint
              :disabled="modoEdicao"
              required
              class="mb-4"
            ></v-text-field>

            <v-text-field
              v-model="aplicacaoAtual.app_type"
              label="Tipo"
              hint="Ex: web, api, mobile, desktop, menu, categoria"
              persistent-hint
              class="mb-4"
            ></v-text-field>

            <v-row class="mb-4">
              <v-col cols="12" md="9">
                <v-text-field
                  v-model="aplicacaoAtual.parent_app"
                  label="Aplicação Pai (Opcional)"
                  readonly
                  hint="Clique no botão para buscar e selecionar"
                  persistent-hint
                  prepend-inner-icon="mdi-folder-outline"
                  clearable
                  @click:clear="aplicacaoAtual.parent_app = null"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3" class="d-flex align-center">
                <v-btn
                  color="primary"
                  variant="tonal"
                  block
                  @click="abrirDialogBuscarPai"
                >
                  <v-icon start>mdi-magnify</v-icon>
                  Buscar Pai
                </v-btn>
              </v-col>
            </v-row>

            <v-text-field
              v-model.number="aplicacaoAtual.order"
              label="Ordem"
              type="number"
              hint="Ordem de exibição (0 = primeiro)"
              persistent-hint
              class="mb-4"
            ></v-text-field>

            <v-textarea
              v-model="aplicacaoAtual.description"
              label="Descrição"
              rows="3"
              hint="Descrição detalhada da funcionalidade"
              persistent-hint
            ></v-textarea>

            <v-alert
              v-if="!modoEdicao"
              type="info"
              variant="tonal"
              density="compact"
              class="mt-4"
            >
              <v-icon start>mdi-information</v-icon>
              Ao criar, a aplicação será adicionada automaticamente a todos os grupos existentes com permissões desabilitadas (N).
            </v-alert>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="fecharDialog">
            Cancelar
          </v-btn>
          <v-btn color="primary" @click="salvarAplicacao" :loading="salvando" :disabled="!formValido">
            <v-icon left>mdi-content-save</v-icon>
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de Confirmação de Exclusão -->
    <v-dialog v-model="dialogExclusao" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">Confirmar Exclusão</v-card-title>
        <v-card-text>
          Tem certeza que deseja excluir a aplicação <strong>{{ aplicacaoParaExcluir?.app_name }}</strong>?
          <br><br>
          <v-alert type="warning" dense text>
            Todas as permissões relacionadas serão removidas!
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="dialogExclusao = false">
            Cancelar
          </v-btn>
          <v-btn color="error" @click="excluirAplicacao" :loading="excluindo">
            <v-icon left>mdi-delete</v-icon>
            Excluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog para Buscar e Selecionar Pai -->
    <v-dialog v-model="dialogBuscarPai" max-width="900px" persistent>
      <v-card>
        <v-card-title class="bg-primary text-white">
          <v-icon class="mr-2">mdi-folder-search</v-icon>
          Buscar Aplicação Pai
        </v-card-title>

        <v-card-text class="pt-4">
          <!-- Filtros -->
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="buscaPai"
                prepend-inner-icon="mdi-magnify"
                label="Buscar"
                placeholder="Digite app_name ou descrição..."
                variant="outlined"
                density="compact"
                clearable
                hide-details
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="3">
              <v-select
                v-model="filtroTipoDialog"
                :items="tiposDisponiveis"
                label="Tipo"
                prepend-inner-icon="mdi-filter"
                variant="outlined"
                density="compact"
                clearable
                hide-details
              ></v-select>
            </v-col>

            <v-col cols="12" md="3">
              <v-select
                v-model="filtroPaiDialog"
                :items="paisDisponiveisDialog"
                item-title="label"
                item-value="value"
                label="Categoria"
                prepend-inner-icon="mdi-folder"
                variant="outlined"
                density="compact"
                clearable
                hide-details
              ></v-select>
            </v-col>
          </v-row>

          <!-- Tabela de Aplicações -->
          <v-data-table
            :headers="[
              { title: 'Descrição', key: 'description', width: '40%' },
              { title: 'App Name', key: 'app_name', width: '35%' },
              { title: 'Tipo', key: 'app_type', width: '15%', align: 'center' },
              { title: 'Ação', key: 'action', width: '10%', align: 'center', sortable: false }
            ]"
            :items="aplicacoesParaBusca"
            :search="buscaPai"
            density="compact"
            class="mt-4"
            :items-per-page="10"
          >
            <template #[`item.description`]="{ item }">
              <div class="text-body-2">
                {{ item.description || 'Sem descrição' }}
              </div>
            </template>

            <template #[`item.app_name`]="{ item }">
              <div class="font-weight-bold font-monospace text-body-2">
                {{ item.app_name }}
              </div>
            </template>

            <template #[`item.app_type`]="{ item }">
              <v-chip size="small" :color="getTypeColor(item.app_type)">
                {{ item.app_type || 'N/A' }}
              </v-chip>
            </template>

            <template #[`item.action`]="{ item }">
              <v-btn
                color="primary"
                size="small"
                variant="tonal"
                @click="selecionarPai(item.app_name)"
                :disabled="item.app_name === aplicacaoAtual.app_name"
              >
                <v-icon size="small">mdi-check</v-icon>
                Selecionar
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="fecharDialogBuscarPai">
            Cancelar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import permissionsService from '@/services/permissionsService';
import { useToast } from 'vue-toastification';

export default {
  name: 'GerenciarAplicacoes',

  data() {
    return {
      aplicacoes: [],
      busca: '',
      filtroTipo: null,
      filtroPai: null,
      dataInicial: null,
      dataFinal: null,
      dialog: false,
      dialogExclusao: false,
      carregando: false,
      salvando: false,
      excluindo: false,
      formValido: false,
      modoEdicao: false,
      aplicacaoAtual: this.aplicacaoVazia(),
      aplicacaoParaExcluir: null,
      dialogBuscarPai: false,
      buscaPai: '',
      filtroTipoDialog: null,
      filtroPaiDialog: null,
      headers: [
        { title: 'Descrição', key: 'description', sortable: true, width: '300px' },
        { title: 'Aplicação', key: 'app_name', sortable: true },
        { title: 'Tipo', key: 'app_type', sortable: true, align: 'center', width: '80px' },
        { title: 'Pai', key: 'parent_app', sortable: true, align: 'center', width: '150px' },
        { title: 'Ordem', key: 'order', sortable: true, align: 'center', width: '80px' },
        { title: 'Grupos', key: 'groups', sortable: false, align: 'center', width: '80px' },
        { title: 'Ações', key: 'actions', sortable: false, align: 'center', width: '120px' }
      ]
    };
  },

  computed: {
    toast() {
      return useToast();
    },

    tiposDisponiveis() {
      const tipos = [...new Set(this.aplicacoes.map(a => a.app_type).filter(t => t))];
      return tipos.sort();
    },

    aplicacoesPais() {
      // Lista de aplicações que podem ser pais (exceto a própria em modo edição)
      return this.aplicacoes
        .filter(a => !this.modoEdicao || a.app_name !== this.aplicacaoAtual.app_name)
        .map(a => ({
          app_name: a.app_name,
          label: `${a.app_name} (${a.app_type})`,
          icon: a.app_type === 'categoria' ? 'mdi-folder' : 'mdi-file'
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },

    paisDisponiveis() {
      // Lista apenas categorias para filtro
      const pais = this.aplicacoes
        .filter(a => a.app_type === 'categoria')
        .map(a => ({
          app_name: a.app_name,
          label: a.app_name
        }));
      
      // Adicionar opção "Sem Pai"
      return [
        { app_name: '__sem_pai__', label: '(Sem Pai)' },
        ...pais.sort((a, b) => a.label.localeCompare(b.label))
      ];
    },

    aplicacoesFiltradas() {
      let filtradas = this.aplicacoes;

      // Filtrar por tipo
      if (this.filtroTipo) {
        filtradas = filtradas.filter(a => a.app_type === this.filtroTipo);
      }

      // Filtrar por pai
      if (this.filtroPai) {
        if (this.filtroPai === '__sem_pai__') {
          filtradas = filtradas.filter(a => !a.parent_app);
        } else {
          filtradas = filtradas.filter(a => a.parent_app === this.filtroPai);
        }
      }

      // Filtrar por data inicial
      if (this.dataInicial) {
        filtradas = filtradas.filter(a => {
          if (!a.created_at) return false;
          const dataApp = new Date(a.created_at).toISOString().split('T')[0];
          return dataApp >= this.dataInicial;
        });
      }

      // Filtrar por data final
      if (this.dataFinal) {
        filtradas = filtradas.filter(a => {
          if (!a.created_at) return false;
          const dataApp = new Date(a.created_at).toISOString().split('T')[0];
          return dataApp <= this.dataFinal;
        });
      }

      return filtradas;
    },

    paisDisponiveisDialog() {
      const categoriasInfo = {
        'menu_producao_geral': { label: '🏭 Produção', ordem: 1 },
        'menu_planejamento_geral': { label: '📅 Planejamento', ordem: 2 },
        'menu_maquinas_geral': { label: '⚙️ Máquinas & Equipamentos', ordem: 3 },
        'menu_expediente_geral': { label: '🕐 Expediente & RH', ordem: 4 },
        'menu_suporte_geral': { label: '🎧 Suporte & Comunicação', ordem: 5 },
        'menu_administracao_geral': { label: '🛡️ Administração', ordem: 6 },
        'menu_usuario_geral': { label: '👥 Usuários', ordem: 7 }
      };

      const pais = [...new Set(this.aplicacoes.map(a => a.parent_app).filter(p => p))];
      
      const resultado = pais.map(pai => ({
        value: pai,
        label: categoriasInfo[pai]?.label || pai,
        ordem: categoriasInfo[pai]?.ordem || 999
      })).sort((a, b) => a.ordem - b.ordem);

      return [
        { value: '__sem_pai__', label: '📱 Outras Aplicações', ordem: 1000 },
        ...resultado
      ];
    },

    aplicacoesParaBusca() {
      let apps = this.aplicacoes;

      // Filtrar por tipo
      if (this.filtroTipoDialog) {
        apps = apps.filter(a => a.app_type === this.filtroTipoDialog);
      }

      // Filtrar por pai
      if (this.filtroPaiDialog) {
        if (this.filtroPaiDialog === '__sem_pai__') {
          apps = apps.filter(a => !a.parent_app);
        } else {
          apps = apps.filter(a => a.parent_app === this.filtroPaiDialog);
        }
      }

      return apps;
    }
  },

  mounted() {
    this.carregarAplicacoes();
  },

  methods: {
    voltarMenu() {
      this.$router.go(-1); // Volta para a página anterior
    },

    aplicacaoVazia() {
      return {
        app_name: '',
        app_type: '',
        parent_app: null,
        order: 0,
        description: ''
      };
    },

    async carregarAplicacoes() {
      this.carregando = true;
      try {
        const response = await permissionsService.listarAplicacoes();
        if (response.status === 'sucesso') {
          this.aplicacoes = response.aplicacoes;
        }
      } catch (error) {
        console.error('Erro ao carregar aplicações:', error);
        this.toast.error('Erro ao carregar aplicações');
      } finally {
        this.carregando = false;
      }
    },

    abrirDialogNovo() {
      this.modoEdicao = false;
      this.aplicacaoAtual = this.aplicacaoVazia();
      this.dialog = true;
    },

    editarAplicacao(aplicacao) {
      this.modoEdicao = true;
      this.aplicacaoAtual = {
        app_name: aplicacao.app_name,
        app_type: aplicacao.app_type || '',
        description: aplicacao.description || '',
        parent_app: aplicacao.parent_app || null,
        order: aplicacao.order ?? 0
      };
      console.log('✏️ Editando aplicação:', this.aplicacaoAtual);
      this.dialog = true;
    },

    async salvarAplicacao() {
      if (!this.$refs.form.validate()) return;

      this.salvando = true;
      try {
        let response;
        
        if (this.modoEdicao) {
          const dados = {
            app_type: this.aplicacaoAtual.app_type,
            description: this.aplicacaoAtual.description,
            parent_app: this.aplicacaoAtual.parent_app || null,
            order: this.aplicacaoAtual.order ?? 0
          };
          
          console.log('📤 Enviando atualização:', dados);
          
          response = await permissionsService.atualizarAplicacao(
            this.aplicacaoAtual.app_name,
            dados
          );
          
          console.log('📥 Resposta:', response);
        } else {
          response = await permissionsService.criarAplicacao(this.aplicacaoAtual);
        }

        if (response.status === 'sucesso') {
          this.toast.success(response.mensagem);
          
          // Mostrar informação sobre grupos vinculados
          if (response.grupos_atualizados) {
            this.toast.info(`Vinculada a ${response.grupos_atualizados} grupo(s)`);
          }
          
          this.fecharDialog();
          this.carregarAplicacoes();
        } else {
          this.toast.error(response.mensagem || 'Erro ao salvar aplicação');
        }
      } catch (error) {
        console.error('Erro ao salvar aplicação:', error);
        this.toast.error('Erro ao salvar aplicação');
      } finally {
        this.salvando = false;
      }
    },

    confirmarExclusao(aplicacao) {
      this.aplicacaoParaExcluir = aplicacao;
      this.dialogExclusao = true;
    },

    async excluirAplicacao() {
      if (!this.aplicacaoParaExcluir) return;

      this.excluindo = true;
      try {
        const response = await permissionsService.deletarAplicacao(this.aplicacaoParaExcluir.app_name);
        
        if (response.status === 'sucesso') {
          this.toast.success(response.mensagem);
          this.dialogExclusao = false;
          this.aplicacaoParaExcluir = null;
          this.carregarAplicacoes();
        } else {
          this.toast.error(response.mensagem || 'Erro ao excluir aplicação');
        }
      } catch (error) {
        console.error('Erro ao excluir aplicação:', error);
        this.toast.error('Erro ao excluir aplicação');
      } finally {
        this.excluindo = false;
      }
    },

    limparFiltros() {
      this.busca = '';
      this.filtroTipo = null;
      this.filtroPai = null;
      this.dataInicial = null;
      this.dataFinal = null;
      this.toast.info('Filtros limpos');
    },

    getTypeColor(type) {
      const colors = {
        'web': 'blue',
        'api': 'green',
        'mobile': 'orange',
        'desktop': 'purple'
      };
      return colors[type] || 'grey';
    },

    fecharDialog() {
      this.dialog = false;
      this.aplicacaoAtual = this.aplicacaoVazia();
      this.$refs.form.resetValidation();
    },

    abrirDialogBuscarPai() {
      this.dialogBuscarPai = true;
    },

    selecionarPai(appName) {
      console.log('🎯 Selecionando pai:', appName);
      console.log('📋 aplicacaoAtual antes:', { ...this.aplicacaoAtual });
      
      this.aplicacaoAtual.parent_app = appName;
      
      console.log('📋 aplicacaoAtual depois:', { ...this.aplicacaoAtual });
      
      this.dialogBuscarPai = false;
      this.buscaPai = '';
      this.filtroTipoDialog = null;
      this.filtroPaiDialog = null;
      this.toast.success(`Pai selecionado: ${appName}`);
    },

    fecharDialogBuscarPai() {
      this.dialogBuscarPai = false;
      this.buscaPai = '';
      this.filtroTipoDialog = null;
      this.filtroPaiDialog = null;
    }
  }
};
</script>
